import { Box, Grid, Paper, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { FinancialsMetrics } from "../../types/Financials";
import { DELTA_ACCENT, TEXT_SECONDARY } from "../colors";

// Trend-chart axis: full "3rd January 2026" dates (used everywhere else in
// the app) are too long to fit several side by side here, and the exact day
// isn't meaningful on a multi-year trend anyway - "Nov 2024" is compact but
// still disambiguates two period-ends landing in the same calendar year
// (which year-only wouldn't) if a company ever changes its accounting
// reference date.
const MONTH_ABBREVIATIONS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const formatChartPeriod = (period: string): string => {
  const [year, month] = period.split("-");
  return `${MONTH_ABBREVIATIONS[parseInt(month, 10) - 1]} ${year}`;
};

// Different taxonomy versions/years tag the same concept under different
// names (e.g. "net assets" shows up as NetAssetsLiabilities in some years,
// ShareholderFunds in others) - each headline metric tries its keys in order
// and uses the first one present for a given period, rather than assuming
// one fixed tag name applies across a company's whole filing history.
const HEADLINE_METRICS: { label: string; keys: string[] }[] = [
  { label: "Turnover / Revenue", keys: ["TurnoverRevenue"] },
  { label: "Profit / Loss", keys: ["ProfitLoss"] },
  {
    label: "Net Assets / Equity",
    keys: [
      "NetAssetsLiabilities",
      "NetAssetsLiabilitiesIncludingPensionAssetLiability",
      "ShareholderFunds",
      "Equity",
    ],
  },
  { label: "Fixed Assets", keys: ["FixedAssets"] },
];

const resolveValue = (metrics: FinancialsMetrics, keys: string[]): number | null => {
  for (const key of keys) {
    if (metrics[key] !== undefined) return metrics[key];
  }
  return null;
};

// These are accounts figures, routinely in the tens of millions - full numbers
// like "44,077,304" don't fit in a small chart's y-axis margin and end up
// clipped/overlapping (all that's visible is the trailing "0,000,000"). Compact
// notation ("44M") keeps labels short regardless of company size.
const COMPACT_FORMAT = new Intl.NumberFormat("en-GB", {
  notation: "compact",
  maximumFractionDigits: 1,
});
// Accounting convention: negatives in brackets rather than a minus sign.
const formatCompact = (value: number | null) => {
  if (value === null) return "";
  const formatted = COMPACT_FORMAT.format(Math.abs(value));
  return value < 0 ? `(${formatted})` : formatted;
};

export const FinancialsCharts = (props: { accounts: Record<string, FinancialsMetrics> }) => {
  const { accounts } = props;
  const periods = Object.keys(accounts).sort(); // chronological (ISO dates sort naturally)

  const charts = HEADLINE_METRICS.map((metric) => ({
    ...metric,
    data: periods.map((period) => resolveValue(accounts[period], metric.keys)),
  })).filter((metric) => metric.data.some((value) => value !== null));

  if (charts.length === 0) {
    return null;
  }

  return (
    <Grid container spacing={2} style={{ padding: 10 }}>
      {charts.map((metric) => (
        <Grid item xs={12} sm={6} key={metric.label}>
          <Paper variant="outlined" style={{ padding: 16 }}>
            <Typography variant="subtitle2" style={{ color: TEXT_SECONDARY, marginBottom: 8 }}>
              {metric.label}
            </Typography>
            <Box style={{ height: 180 }}>
              <LineChart
                xAxis={[
                  {
                    scaleType: "point",
                    data: periods,
                    valueFormatter: formatChartPeriod,
                    label: undefined,
                  },
                ]}
                yAxis={[{ valueFormatter: formatCompact }]}
                series={[
                  {
                    data: metric.data,
                    color: DELTA_ACCENT,
                    showMark: true,
                    valueFormatter: formatCompact,
                  },
                ]}
                height={180}
                margin={{ left: 50, right: 20, top: 10, bottom: 30 }}
              />
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

export default FinancialsCharts;
